import React from "react";
import { useDispatch, useSelector } from "react-redux";
import ActionLogger from "action-logger";
import classNames from "classnames";
import { analytics } from "rtr-constants";
import PropTypes from "prop-types";
import RtrImage from "components/source/shared/rtr-image";
import PlusSign from "images/plus_sign.svg";

import { hasFileSizeError as hasFileSizeErrorAction } from "actions/shared-actions";
import happinessSurveyActions from "actions/happiness-survey-actions";

const maxPhotosLength = 3;

function ReviewPhotoUploader({ bookingId }) {
  const dispatch = useDispatch();
  const [rotatedPhotos, setRotatedPhotos] = React.useState({});
  const photoUploader = React.createRef();

  const hasFileSizeError = useSelector(state => state.hasFileSizeError) || false;
  const loadingPhotos = useSelector(state => state.loadingPhotos) || false;
  const loadingRotatedPhoto = useSelector(state => state.loadingRotatedPhoto) || false;
  const reviewPhotoError = useSelector(state => state.reviewPhotoError) || false;

  const reviewPhotos = useSelector(state => {
    const survey = state.happinessSurvey.itemSurveys.find(item => item.bookingId === bookingId);
    return survey?.reviewPhotos || [];
  });

  const uploadPhotoHandler = e => {
    e.preventDefault();

    dispatch(happinessSurveyActions.updateReviewPhotoFailure(null));
    dispatch(hasFileSizeErrorAction(false));

    const remainingPhotoCount = maxPhotosLength - reviewPhotos.length;
    const files = Array.from(e.target.files).slice(0, remainingPhotoCount);

    files.forEach(file => {
      const reader = new FileReader();
      const photoSizeLimitInMB = 5242800;

      reader.onloadend = () => {
        if (file.size < photoSizeLimitInMB) {
          const image = new Image();
          image.onload = () => {
            ActionLogger.logAction({ object_type: "review_your_item", action: "add_photo" });
            dispatch(
              happinessSurveyActions.submitReviewPhoto(bookingId, {
                data: reader.result,
                filename: file.name,
                type: file.type,
              })
            );
          };
          image.src = reader.result;
        } else {
          dispatch(hasFileSizeErrorAction(true));
        }
      };

      reader.readAsDataURL(file);
    });

    photoUploader.current.value = "";
  };

  const deletePhotoHandler = (photo, e) => {
    e.preventDefault();
    dispatch(happinessSurveyActions.setLoadingPhotos(true));
    dispatch(happinessSurveyActions.updateReviewPhotoFailure(null));

    dispatch(
      happinessSurveyActions.deleteReviewPhoto(
        photo.photoId,
        () => {
          dispatch(happinessSurveyActions.setLoadingPhotos(false));
          dispatch(happinessSurveyActions.updateReviewPhotoFailure(null));
          ActionLogger.logAction({
            object_type: analytics.OBJECT_TYPE.REVIEWS,
            action: "delete_review_photo",
          });
        },
        response => {
          dispatch(happinessSurveyActions.setLoadingPhotos(false));
          dispatch(happinessSurveyActions.updateReviewPhotoFailure("Unable to delete photo. Please try again."));
          ActionLogger.logAction({
            object_type: analytics.OBJECT_TYPE.REVIEWS,
            action: analytics.ACTION_TYPE.ERROR,
            errorMessage: "postDeleteImage failed: " + JSON.stringify(response),
          });
        },
        bookingId
      )
    );
  };

  const onRotatePhoto = (photoId, e) => {
    e.preventDefault();
    dispatch(happinessSurveyActions.rotateReviewPhoto(photoId));

    setRotatedPhotos(prev => {
      const newRotation = ((prev[photoId] || 0) + 90) % 360;
      return { ...prev, [photoId]: newRotation };
    });
  };

  const renderAddPhotoButton = () => {
    if (reviewPhotos.length >= maxPhotosLength) return null;
    let loading;

    if (loadingPhotos) {
      loading = (
        <div className="photo-uploader__add-button">
          <div className="photo-uploader__add-button__loading"></div>
        </div>
      );
    }

    return (
      <label
        htmlFor="photo-uploader__input"
        className="photo-uploader__input__label"
        data-test-id="photo-uploader__input_label">
        {loading}
        {loadingPhotos && reviewPhotos.length === maxPhotosLength - 1 ? null : (
          <div className="photo-uploader__add-button">
            <PlusSign />
            <div className="universal-xsmall">Add a photo</div>
          </div>
        )}
      </label>
    );
  };

  return (
    <div className="photo-uploader" data-test-id="photo-uploader">
      <input
        onChange={uploadPhotoHandler}
        type="file"
        name="photos"
        ref={photoUploader}
        id="photo-uploader__input"
        className="photo-uploader__input"
        accept="image/*"
        multiple
        data-test-id="photo-uploader__input"
      />

      <div className="photo-uploader__photos">
        {reviewPhotos.map((photo, i) => {
          const loading = loadingRotatedPhoto === photo.photoId;

          const imageClassName = classNames("photo-uploader__photo__image", {
            "photo-uploader__photo__image--loading": loading,
            [`photo-uploader__photo__image--rotate-${rotatedPhotos[photo.photoId]}`]: rotatedPhotos[photo.photoId],
          });

          return (
            <div className="photo-uploader__photo" key={`photo-${i}`}>
              <button
                type="button"
                className="photo-uploader__rotate-button"
                onClick={e => onRotatePhoto(photo.photoId, e)}>
                ↻
              </button>
              <div className={imageClassName} data-test-id="photo-uploader__photo__image">
                <RtrImage src={photo.photoUrl} alt={`review photo ${i}`} />
              </div>
              <button
                type="button"
                className="photo-uploader__remove-button"
                onClick={e => deletePhotoHandler(photo, e)}>
                ✕
              </button>
            </div>
          );
        })}

        {renderAddPhotoButton()}
      </div>

      {hasFileSizeError && (
        <div className="file-size-error universal-small">
          <p className="file-size-error-heading">Your photo is too big</p>
          <p className="file-size-error-subtext xsmall">
            Upload a new photo that does not exceed <br />
            the 5MB limit.
          </p>
        </div>
      )}

      {reviewPhotoError && <div className="photo-uploader__error">{reviewPhotoError}</div>}
    </div>
  );
}

ReviewPhotoUploader.propTypes = {
  bookingId: PropTypes.string.isRequired,
};

export default ReviewPhotoUploader;
